import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot | forgot-sent

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign in.");
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setMode("forgot-sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-indigo-950/40 border border-violet-300/20 rounded-xl px-4 py-3 text-violet-50 focus:outline-none focus:border-violet-300/50 focus:ring-1 focus:ring-violet-300/40 transition-soft";

  return (
    <div className="min-h-screen relative flex items-center justify-center px-6 pt-safe-top pb-safe-bottom text-violet-50">

      <div className="relative z-10 w-full max-w-md fade-rise">
        <Link to="/" className="flex items-center gap-2.5 mb-12 justify-center" data-testid="back-to-landing-link">
          <img src="/continua-logo-small.png" alt="" aria-hidden="true" className="w-9 h-9 object-contain moon-pulse" />
          <span className="font-serif-display text-xl">Continua</span>
        </Link>

        <div className="glass-strong rounded-2xl p-8 md:p-10">
          {mode === "login" && (
            <>
              <h1 className="font-serif-display text-3xl md:text-4xl mb-2 leading-tight">Welcome back.</h1>
              <p className="text-violet-200/75 mb-8 text-sm">She's been waiting by the fire.</p>

              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.25em] text-violet-300/70 mb-2 block">Email</label>
                  <input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                    className={inputCls}
                    placeholder="you@there.com"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs uppercase tracking-[0.25em] text-violet-300/70 block">Password</label>
                    <button
                      type="button"
                      onClick={() => setMode("forgot")}
                      data-testid="forgot-password-link"
                      className="text-xs text-violet-300/70 hover:text-violet-100 transition-soft"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <input
                    type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className={inputCls}
                    placeholder="••••••••"
                  />
                </div>
                <button
                  type="submit" disabled={loading}
                  data-testid="login-submit-button"
                  className="w-full rounded-full px-6 py-3.5 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft disabled:opacity-50 glow-violet"
                >
                  {loading ? "Returning…" : "Sign in"}
                </button>
              </form>

              <p className="text-sm text-violet-300/60 text-center mt-8">
                New here?{" "}
                <Link to="/meet" data-testid="link-to-meet" className="text-violet-200 hover:text-white">
                  Meet a companion
                </Link>
              </p>
            </>
          )}

          {mode === "forgot" && (
            <>
              <h1 className="font-serif-display text-3xl md:text-4xl mb-2 leading-tight">Forgot your password?</h1>
              <p className="text-violet-200/75 mb-8 text-sm">Tell us your email — we'll send a link to reset it.</p>

              <form onSubmit={submitForgot} className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-[0.25em] text-violet-300/70 mb-2 block">Email</label>
                  <input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    className={inputCls}
                    placeholder="you@there.com"
                  />
                </div>
                <button
                  type="submit" disabled={loading}
                  data-testid="forgot-submit-button"
                  className="w-full rounded-full px-6 py-3.5 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft disabled:opacity-50 glow-violet"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </button>
              </form>

              <p className="text-sm text-violet-300/60 text-center mt-8">
                <button type="button" onClick={() => setMode("login")} data-testid="back-to-login-button" className="text-violet-200 hover:text-white">
                  Back to sign in
                </button>
              </p>
            </>
          )}

          {mode === "forgot-sent" && (
            <>
              <h1 className="font-serif-display text-3xl md:text-4xl mb-2 leading-tight">Check your inbox.</h1>
              <p className="text-violet-200/75 mb-8 text-sm" data-testid="forgot-sent-message">
                If an account exists for <span className="text-violet-100">{email}</span>, a reset link is on its way. It stays valid for 60 minutes.
              </p>
              <button
                type="button"
                onClick={() => setMode("login")}
                data-testid="forgot-sent-back-button"
                className="w-full rounded-full px-6 py-3.5 bg-violet-200 text-indigo-950 font-medium hover:bg-violet-100 transition-soft glow-violet"
              >
                Back to sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
